import { LiveAttributionWindow, LIVE_ATTRIBUTION_WINDOW_ORDER } from '../types'

/**
 * Sincronização dos filtros do dashboard com a query string, pra URL ser
 * compartilhável: quem abrir o link vê empresa/segmento/plano, período e
 * janela de atribuição já selecionados — mas o processamento continua manual
 * (a pessoa ainda precisa clicar em "Processar").
 *
 * Parâmetros:
 *  - company: key da empresa selecionada
 *  - segment: filtro de segmento (modo grupo)
 *  - plan:    filtro de plano (modo grupo)
 *  - start / end: período (YYYY-MM-DD)
 *  - window:  janela de atribuição do Live Commerce (4h..7d)
 *  - mode:    produto ativo ('stories' | 'live')
 *
 * A URL usa replaceState (não pushState) de propósito: cada ajuste de filtro
 * NÃO deve virar uma entrada no histórico do navegador — voltar deve sair da
 * página, não desfazer 15 cliques de filtro.
 */
export interface UrlFilters {
  companyKey: string | null
  segment: string | null
  planType: string | null
  startDate: string | null
  endDate: string | null
  liveAttributionWindow: LiveAttributionWindow | null
  productMode: 'stories' | 'live' | null
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function asIsoDate(value: string | null): string | null {
  return value && ISO_DATE_RE.test(value) ? value : null
}

function asWindow(value: string | null): LiveAttributionWindow | null {
  return value && (LIVE_ATTRIBUTION_WINDOW_ORDER as string[]).includes(value)
    ? (value as LiveAttributionWindow)
    : null
}

function asProductMode(value: string | null): 'stories' | 'live' | null {
  return value === 'stories' || value === 'live' ? value : null
}

/** Lê e valida os filtros da URL atual. Valores inválidos viram null
 *  (caem no default do app) em vez de quebrar a inicialização. */
export function readUrlFilters(): UrlFilters {
  const params = new URLSearchParams(window.location.search)
  return {
    companyKey: params.get('company'),
    segment: params.get('segment'),
    planType: params.get('plan'),
    startDate: asIsoDate(params.get('start')),
    endDate: asIsoDate(params.get('end')),
    liveAttributionWindow: asWindow(params.get('window')),
    productMode: asProductMode(params.get('mode')),
  }
}

/** Espelha o estado atual dos filtros na URL (replaceState). Parâmetros
 *  vazios são omitidos pra URL ficar limpa. */
export function writeUrlFilters(filters: UrlFilters): void {
  const params = new URLSearchParams()
  if (filters.productMode) params.set('mode', filters.productMode)
  if (filters.companyKey) params.set('company', filters.companyKey)
  if (filters.segment) params.set('segment', filters.segment)
  if (filters.planType) params.set('plan', filters.planType)
  if (filters.startDate) params.set('start', filters.startDate)
  if (filters.endDate) params.set('end', filters.endDate)
  if (filters.liveAttributionWindow) params.set('window', filters.liveAttributionWindow)

  const query = params.toString()
  const next = `${window.location.pathname}${query ? `?${query}` : ''}`
  const current = `${window.location.pathname}${window.location.search}`
  if (next !== current) {
    window.history.replaceState(null, '', next)
  }
}
